import { api } from '../client';
import type {
  AttendanceMark,
  AttendanceSource,
  AttendanceStatus,
  AttendanceSummary,
  AttendanceType,
  Register,
  EmployeeAttendanceMark,
  EmployeeAttendanceStatus,
  UUID,
} from '@/types/domain';

export interface MarkAttendancePayload {
  sectionId: UUID;
  attendanceDate: string;
  attendanceType: AttendanceType;
  /** Required for PERIOD, must be omitted for DAILY. */
  periodId?: UUID;
  marks: { enrollmentId: UUID; status: AttendanceStatus; remarks?: string }[];
}

export const attendanceApi = {
  /**
   * A section's register, including children not yet marked.
   *
   * Built from the placements covering that date, so a register for a past day
   * lists the children who were in the class then rather than the ones there now.
   */
  register: (params: {
    sectionId: UUID;
    date: string;
    type?: AttendanceType;
    periodId?: UUID;
  }) => api.get<Register>('/attendance/register', { params }),

  /** Marking again corrects the existing entries rather than duplicating them. */
  mark: (payload: MarkAttendancePayload) => api.post<Register>('/attendance', payload),

  forStudent: (studentId: UUID, from: string, to: string) =>
    api.get<AttendanceMark[]>(`/attendance/students/${studentId}`, { params: { from, to } }),

  summary: (studentId: UUID, from: string, to: string) =>
    api.get<AttendanceSummary>(`/attendance/students/${studentId}/summary`, {
      params: { from, to },
    }),
};

export interface EmployeeAttendancePayload {
  employeeId: UUID;
  attendanceDate: string;
  status: EmployeeAttendanceStatus;
  checkInAt?: string;
  checkOutAt?: string;
  source?: AttendanceSource;
  remarks?: string;
}

export const employeeAttendanceApi = {
  forDay: (date: string) => api.get<EmployeeAttendanceMark[]>('/employee-attendance', { params: { date } }),
  forEmployee: (employeeId: UUID, from: string, to: string) =>
    api.get<EmployeeAttendanceMark[]>(`/employee-attendance/employees/${employeeId}`, {
      params: { from, to },
    }),
  /** One record per person per day; recording again corrects the existing row. */
  record: (payload: EmployeeAttendancePayload) =>
    api.post<EmployeeAttendanceMark>('/employee-attendance', payload),
};
